import { useState, useEffect, useCallback } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  logoutUser,
} from "context";

// Auth utilities
import { apiLogout } from "api/auth";
import { apiGetTindakanNotis } from "api/notis";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode, user } =
    controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();

  // Counts for invoices (inbois) and unique accounts with tempoh_notis >= 14 and not yet makluman_uuu
  const [uuuPendingInbois, setUuuPendingInbois] = useState(0);
  const [uuuPendingAccounts, setUuuPendingAccounts] = useState(0);

  const isUEU = ((user && (user.bahagian || user.department)) || "").toString().toLowerCase() === "ueu";

  const refreshUuuPending = useCallback(async () => {
    try {
      if (!isUEU) {
        setUuuPendingInbois(0);
        setUuuPendingAccounts(0);
        return;
      }
      const res = await apiGetTindakanNotis();
      const rows = Array.isArray(res?.rows) ? res.rows : [];
      let inbois = 0;
      const accSet = new Set();
      for (const r of rows) {
        const tempoh = typeof r.tempoh_notis_hari === 'number' ? r.tempoh_notis_hari : null;
        const makluman = r.makluman_uuu === true; // true if mark_notis = 2
        if (!makluman && tempoh !== null && tempoh >= 14) {
          inbois += 1;
          if (r.no_akaun != null) accSet.add(String(r.no_akaun));
        }
      }
      setUuuPendingInbois(inbois);
      setUuuPendingAccounts(accSet.size);
    } catch (_) {
      // silent
      setUuuPendingInbois(0);
      setUuuPendingAccounts(0);
    }
  }, [isUEU]);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  // Load UUU pending count on mount and when path changes
  useEffect(() => {
    refreshUuuPending();
    // Optionally refresh every 60s
    const t = setInterval(refreshUuuPending, 60000);
    return () => clearInterval(t);
  }, [refreshUuuPending]);

  // Refresh when global context indicates UUU pending has changed
  useEffect(() => {
    refreshUuuPending();
  }, [controller.uuuPendingRefreshAt, refreshUuuPending]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => {
    const canShowUuuNotif = isUEU && uuuPendingInbois > 0;
    if (canShowUuuNotif) setOpenMenu(event.currentTarget);
  };
  const handleCloseMenu = () => setOpenMenu(false);
  const handleOpenProfileMenu = (event) => setOpenProfileMenu(event.currentTarget);
  const handleCloseProfileMenu = () => setOpenProfileMenu(false);

  const { setLayout } = require("context");
  const handleLogout = async () => {
    try {
      await apiLogout();
      logoutUser(dispatch);
      setLayout(dispatch, "auth");
      handleCloseProfileMenu();
      window.location.href = "/authentication/sign-in";
    } catch (error) {
      console.error("Logout error:", error);
      // Force logout even if API call fails
      logoutUser(dispatch);
      setLayout(dispatch, "auth");
      handleCloseProfileMenu();
      window.location.href = "/authentication/sign-in";
    }
  };

  // Render the notifications menu
  const renderMenu = () => {
    const canShowUuuNotif = isUEU && uuuPendingInbois > 0;
    if (!canShowUuuNotif) return null;
    return (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{
        mt: 2,
        // Reduce width of the notification box ~25% from the natural content width
        // by constraining the paper width. Also make it responsive.
        '& .MuiPaper-root': {
          width: 420,
          maxWidth: '90vw',
          backgroundColor: '#E3F2FD', // light blue background
          borderRadius: '10px', // rounded corners for the notification box
        },
      }}
    >
      <NotificationItem
        icon={<Icon color="error">priority_high</Icon>}
        title={
          <MDTypography
            variant="body2"
            fontSize="0.9rem"
            sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3 }}
          >
            <MDBox component="span" sx={{ color: 'error.main', fontWeight: 'bold' }}>
              {uuuPendingAccounts}
            </MDBox>{" "}
            akaun dengan jumlah {" "}
            <MDBox component="span" sx={{ color: 'error.main', fontWeight: 'bold' }}>
              {uuuPendingInbois}
            </MDBox>{" "}
            inbois belum dimaklumkan kepada UUU
          </MDTypography>
        }
        onClick={() => {
          handleCloseMenu();
          navigate('/notis-tunggakan?tab=tindakan');
        }}
      />
    </Menu>
  ); };

  // Render the profile menu
  const renderProfileMenu = () => (
    <Menu
      anchorEl={openProfileMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openProfileMenu)}
      onClose={handleCloseProfileMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>logout</Icon>} title="Logout" onClick={handleLogout} />
    </Menu>
  );

  // Styles for the navbar icons - gold theme
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      // Use gold color for icons in navbar to match theme
      if (light) {
        return white.main; // Keep white for light backgrounds
      }
      return "#f4c542"; // Gold color for icons
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"} display="flex" alignItems="center">
              {user && (
                <MDBox display="flex" alignItems="center" mr={1}>
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    color={light ? "white" : "text"}
                    sx={{ lineHeight: 1, textTransform: "uppercase" }}
                  >
                    {user.id_pengguna}
                  </MDTypography>
                </MDBox>
              )}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="profile-menu"
                aria-haspopup="true"
                onClick={handleOpenProfileMenu}
              >
                <Icon sx={iconsStyle}>account_circle</Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Badge color="error" badgeContent={isUEU ? uuuPendingInbois : 0} overlap="circular">
                  <Icon sx={iconsStyle}>notifications</Icon>
                </Badge>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {renderMenu()}
              {renderProfileMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
